// Copyright 2023 Sorama B.V.

import { autoinject, bindable, computedFrom, customElement } from "aurelia-framework";

interface ISelectableRow {
    isSelected: boolean;
}

interface IGroup {
    name: string;
    rows: ISelectableRow[];
    collapsed: boolean;
}

customElement("grid-table");
@autoinject
export class GridTable {
    @bindable
    public selectable = false;

    @bindable
    public multiSelect = false;

    // The key of the data item to group by
    @bindable
    public groupBy = "";

    @bindable
    public gridTemplateColumns = "";

    @bindable({ changeHandler: "dataChanged" })
    public data: ISelectableRow[] = [];

    private lastIndexClicked = -1;

    @bindable({ defaultBindingMode: "twoWay" })
    public selectedRows: ISelectableRow[] = [];

    constructor(private readonly element: Element) {}

    private get dataInGroupOrder() {
        return this.groups.reduce((acc, group) => acc.concat(group.rows), []);
    }

    @computedFrom("data", "groupBy")
    private get groups() {
        const groups = [];
        this.data.forEach((item) => {
            const key = item[this.groupBy];
            if (!groups.find((g) => g.name === key)) {
                groups.push({ name: key, rows: [], collapsed: false });
            }
            groups.find((g) => g.name === key).rows.push(item);
        });

        return groups;
    }

    private setSelectedRows(): void {
        this.selectedRows = this.data.filter((row) => row.isSelected);
    }

    private dataChanged(newData: ISelectableRow[], oldData: ISelectableRow[]): void {
        // if items have been removed set isSelected to false
        if (oldData && oldData.length > newData.length) {
            oldData.forEach((row, index) => {
                if (index >= newData.length) row.isSelected = false;
            });
        }
        this.setSelectedRows();
    }

    @computedFrom("gridTemplateColumns", "data")
    private get columnStyle() {
        if (!this.gridTemplateColumns) {
            // Looks how many "th" tags it has and provide equal spacing for each column
            const columnCount = this.element.getElementsByTagName("th").length;
            if (columnCount === 0) return;
            this.gridTemplateColumns = `repeat(${columnCount}, 1fr)`;
        }
        return "grid-template-columns: " + this.gridTemplateColumns;
    }

    private clickedRow(row: ISelectableRow, index: number) {
        if (!this.selectable) return;

        if (this.groupBy) {
            // index is the index of the row in the group, we need to find the index of the row in the data array
            index = this.dataInGroupOrder.findIndex((r) => r === row);
        }

        if (this.multiSelect && this.lastIndexClicked !== -1 && (window.event as MouseEvent).shiftKey) {
            // when the user is holding shift, select all rows between the last clicked row and the current clicked row
            const min = Math.min(this.lastIndexClicked, index);
            const max = Math.max(this.lastIndexClicked, index);
            this.dataInGroupOrder
                .filter((row, index) => index >= min && index <= max)
                .forEach((row) => (row.isSelected = true));
        } else if (this.multiSelect && this.lastIndexClicked !== -1 && (window.event as MouseEvent).ctrlKey) {
            row.isSelected = !row.isSelected;
        } else {
            // if it is the only one on, turn it off, otherwise turn it on and turn off the rest
            if (row.isSelected && this.data.filter((r) => r.isSelected).length === 1) {
                row.isSelected = false;
            } else {
                this.data.forEach((r) => (r.isSelected = false));
                row.isSelected = true;
            }
        }
        this.lastIndexClicked = index;
        this.setSelectedRows();
    }

    private clickedGroup(group: IGroup) {
        group.collapsed = !group.collapsed;
    }
}
